// ── Filter Data Types ──

export type FilterDataType = 'text' | 'number' | 'boolean' | 'date' | 'education' | 'experience';

// ── Operators ──

export type TextOperator = 'contains' | 'not_contains' | 'equals' | 'not_equals' | 'is_empty' | 'is_not_empty' | 'is_one_of' | 'is_none_of' | 'contains_one_of';
export type NumberOperator = 'equals' | 'not_equals' | 'gt' | 'gte' | 'lt' | 'lte' | 'between';
export type BooleanOperator = 'is_true' | 'is_false';
export type DateOperator = 'before' | 'after' | 'between';

export type FilterOperator = TextOperator | NumberOperator | BooleanOperator | DateOperator;

// ── Combinator ──

export type FilterCombinator = 'and' | 'or';

// ── Rule ──

export interface FilterRule {
  id: string;
  kind: 'rule';
  combinator: FilterCombinator;
  field: string;
  operator: FilterOperator;
  value: string;
  values?: string[];  // multi-value for is_one_of / is_none_of / contains_one_of
  valueTo?: string; // second value for "between" operator
  yearValue?: string;   // education composite: year (or range start)
  yearValueTo?: string; // education composite: year range end
  durationValue?: string;   // education composite: duration in years (or range start)
  durationValueTo?: string; // education composite: duration range end
}

// ── Recursive Group ──

export interface FilterGroup {
  id: string;
  kind: 'group';
  combinator: FilterCombinator;
  children: FilterNode[];
}

export type FilterNode = FilterRule | FilterGroup;

// ── Field Definition ──

export interface FilterFieldDef {
  field: string;
  label: string;
  type: FilterDataType;
  operators: FilterOperator[];
  /** Optional grouping label for the field selector dropdown */
  group?: string;
}

// ── Operator labels (for UI display) ──

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  contains: 'Contains',
  not_contains: 'Does not contain',
  equals: 'Equals',
  not_equals: 'Not equals',
  is_empty: 'Is empty',
  is_not_empty: 'Is not empty',
  is_one_of: 'Is one of',
  is_none_of: 'Is none of',
  contains_one_of: 'Contains one of',
  gt: 'Greater than',
  gte: 'Greater or equal',
  lt: 'Less than',
  lte: 'Less or equal',
  between: 'Between',
  is_true: 'Yes',
  is_false: 'No',
  before: 'Before',
  after: 'After',
};

/** Operators that require no value input */
export const VALUELESS_OPERATORS: FilterOperator[] = ['is_empty', 'is_not_empty', 'is_true', 'is_false'];

/** Operators that require a second "to" value */
export const RANGE_OPERATORS: FilterOperator[] = ['between'];

/** Operators that use the `values` array instead of `value` */
export const MULTI_VALUE_OPERATORS: FilterOperator[] = ['is_one_of', 'is_none_of', 'contains_one_of'];
